// Auto-derivation layer — Acceptance Pass (P3).
//
// Pure function: given the chosen profile payloads, derive every length /
// capacity the kill sheet needs.  User overrides take precedence — a cell
// flagged source:"user" is never recomputed by this function.
//
// Cell shape: { value, source: "derived" | "user" }.
// Empty / missing inputs render the cell as { value: null, source: "derived" }.
//
// Drill Pipe geometry — ROOT-CAUSE CORRECTION (Sep 2026, ticket 255586).
// DP OD/ID used to be read from ONE place: a "Drill Pipe" component inside
// the bound legacy BHA profile, so the three DP capacities stayed blank
// whenever the BHA had no such row. They now come from
// `./drillPipeResolver`: the active Rig Engineering Session's Drill String
// first, the well's active Drill String second, the legacy BHA component
// last. A frozen snapshot on `session.drillPipe` (stamped when a Kill Sheet
// is created / saved / printed — see sessionStore.freezeDrillPipeGeometry)
// always wins over a live lookup, so a historical sheet never changes when
// the live Drill String is edited later. The capacity arithmetic below is
// untouched; only the data-source path for DP OD/ID changed.

import { resolveDrillPipeGeometry, drillPipeDimsForUnits } from "./drillPipeResolver";
import { resolveSurfaceLineVolumeBbl } from "./surfaceLineVolumeResolver";
import { resolveBopContext } from "./bopContextResolver";

const IMP_DIVISOR = 1029.4;             // (in²·ft → bbl)
const SI_INTERNAL = (id_mm) =>           // m³/m for round pipe ID in mm
  Math.PI * Math.pow(id_mm / 1000, 2) / 4;

const _num = (v) => {
  if (v && typeof v === "object" && "value" in v) v = v.value;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

function _pipeCap(idDim, units) {
  if (idDim == null) return null;
  return units === "SI" ? SI_INTERNAL(idDim) : (idDim * idDim) / IMP_DIVISOR;
}
function _annCap(dh, dp, units) {
  if (dh == null || dp == null) return null;
  return units === "SI"
    ? (Math.PI / 4) * (Math.pow(dh / 1000, 2) - Math.pow(dp / 1000, 2))
    : (dh * dh - dp * dp) / IMP_DIVISOR;
}
function _firstComponentOfKind(bha, kindMatcher) {
  if (!bha || !Array.isArray(bha.components)) return null;
  return bha.components.find((c) => kindMatcher(c.kind || ""));
}
function _sumLengthOfKind(bha, kindMatcher) {
  if (!bha || !Array.isArray(bha.components)) return 0;
  return bha.components.reduce((s, c) =>
    kindMatcher(c.kind || "") ? s + (_num(c.length) || 0) : s, 0);
}
function _sumBhaLength(bha) {
  if (!bha || !Array.isArray(bha.components)) return 0;
  return bha.components.reduce((s, c) => s + (_num(c.length) || 0), 0);
}
function _firstCasingString(csg) {
  return csg && Array.isArray(csg.strings) ? csg.strings[0] : null;
}

/**
 * deriveSession(session, overrides?, opts?) →
 *   { lengths, capacities, drillPipe, hwdpGeometry, dcGeometry,
 *     surfaceLineVolume, wellContext, composerInput }
 * `overrides` is the user's edited copy; any non-empty value there is taken
 * as authoritative.  The `sources` object lets the UI render the "↻ Reset to
 * derived" link next to any user-overridden cell.
 * `opts.deps` (tests only) forwards injectable storage readers to the Drill
 * Pipe resolver.
 */
export function deriveSession(session, overrides = {}, opts = {}) {
  const u = session.units || "IMP";
  const well = session.well || {};
  const bha  = session.bha || {};
  const csg  = session.casing || {};
  const mud  = session.mud || {};
  const ops  = session.opstate || {};

  // Resolve dynamic vs static fields per the "single source of truth" rules.
  const currentMd  = _num(ops.currentMd)  ?? _num(well.currentMd);
  const currentTvd = _num(ops.currentTvd) ?? _num(well.currentTvd);
  const shoeMd     = _num(well.shoeMd);
  const shoeTvd    = _num(well.shoeTvd);
  const mudWeight  = _num(ops.currentMudDensity) ?? _num(mud.density);

  const bhaLen     = _sumBhaLength(bha);
  const hwdpLen    = _sumLengthOfKind(bha, (k) => /^hwdp/i.test(k));
  const dcLen      = _sumLengthOfKind(bha, (k) => /drill\s*collar|^dc$/i.test(k));
  const otherBha   = bhaLen - hwdpLen - dcLen;
  // Drill-pipe length = MD − (DC + HWDP + other-BHA components above bit)
  const dpLen      = currentMd != null
    ? Math.max(0, currentMd - bhaLen) : null;

  const openHoleTotal = (currentMd != null && shoeMd != null)
    ? Math.max(0, currentMd - shoeMd) : null;
  const dpHwdpOpenHole = (openHoleTotal != null)
    ? Math.max(0, openHoleTotal - dcLen) : null;
  const dpInCasing = shoeMd;
  const dcOpenHole = dcLen;

  // Drill Pipe geometry — frozen snapshot wins, else live canonical resolution
  // (session Drill String → well active Drill String → BHA component).
  const dpGeom = (session.drillPipe && session.drillPipe.frozen)
    ? session.drillPipe
    : resolveDrillPipeGeometry(session, opts.deps);
  const { od: dpOd, id: dpId } = drillPipeDimsForUnits(dpGeom, u);

  // Remaining component diameters for capacity calculation (unchanged).
  const hwdpComp = _firstComponentOfKind(bha, (k) => /^hwdp/i.test(k));
  const dcComp   = _firstComponentOfKind(bha, (k) => /drill\s*collar|^dc$/i.test(k));
  const csgFirst = _firstCasingString(csg);
  const holeSize = _num(ops.holeSize) ?? _num(well.holeSize);

  const capDpInternal   = _pipeCap(dpId,                 u);
  const capHwdpInternal = _pipeCap(_num(hwdpComp?.idIn), u);
  const capDcInternal   = _pipeCap(_num(dcComp?.idIn),   u);
  const capDpInCasing   = _annCap(_num(csgFirst?.idIn), dpOd, u);
  const capDpHwdpInOh   = _annCap(holeSize,             dpOd, u);
  const capDcInOh       = _annCap(holeSize,             _num(dcComp?.odIn), u);
  const capHwdpInOh     = _annCap(holeSize,             _num(hwdpComp?.odIn), u);

  // Surface Line Volume — pump discharge to drillstring entry (standpipe +
  // rotary hose / manifold). Canonical source only (Rig Data Sheet via the
  // active Rig Engineering Session); no legacy fallback exists for this
  // field. Display-only alongside its own stroke count -- deliberately NOT
  // folded into any existing composer.js volume/stroke total (see
  // ./surfaceLineVolumeResolver.js header for the unit-safety reasoning).
  const surfaceLineGeom = resolveSurfaceLineVolumeBbl(u, opts.deps);
  // BOP reference context -- see ./bopContextResolver.js. Display-only,
  // folded into wellContext below; no formula/composer changes.
  const bopCtx = resolveBopContext(opts.deps);
  const pumpIdx = session.pump?.selectedIndex ?? 0;
  const pumpSel = (session.pump?.pumps || [])[pumpIdx] || {};
  // Bound Pump Profile is the engineering source of truth; OpState's
  // currentPumpDisplacement (pulled from Engineering Setup on the OpState
  // form) is the operational fallback when no pump is bound yet or its
  // displacement was never entered -- same ops ?? pattern as currentMd /
  // mudWeight above.
  const pumpDisp = _num(pumpSel.displacement) ?? _num(ops.currentPumpDisplacement);
  const surfaceLineStrokes = (surfaceLineGeom.valueBbl != null && pumpDisp != null && pumpDisp > 0)
    ? surfaceLineGeom.valueBbl / pumpDisp
    : null;

  const _resolve = (key, derivedValue) => {
    const ov = overrides[key];
    if (ov != null && ov !== "" && Number.isFinite(parseFloat(ov))) {
      return { value: parseFloat(ov), source: "user" };
    }
    return { value: derivedValue, source: "derived" };
  };

  const lengths = {
    bha:               _resolve("bha",               bhaLen),
    dp:                _resolve("dp",                dpLen),
    hwdp:              _resolve("hwdp",              hwdpLen),
    dc:                _resolve("dc",                dcLen),
    dpHwdpOpenHole:    _resolve("dpHwdpOpenHole",    dpHwdpOpenHole),
    dcOpenHole:        _resolve("dcOpenHole",        dcOpenHole),
    dpInCasing:        _resolve("dpInCasing",        dpInCasing),
    openHoleTotal:     _resolve("openHoleTotal",     openHoleTotal),
  };
  const capacities = {
    dpInternal:        _resolve("capDpInternal",     capDpInternal),
    hwdpInternal:      _resolve("capHwdpInternal",   capHwdpInternal),
    dcInternal:        _resolve("capDcInternal",     capDcInternal),
    dpInCasing:        _resolve("capDpInCasing",     capDpInCasing),
    dpHwdpInOpenHole:  _resolve("capDpHwdpInOh",     capDpHwdpInOh),
    dcInOpenHole:      _resolve("capDcInOh",         capDcInOh),
    hwdpInOpenHole:    _resolve("capHwdpInOh",       capHwdpInOh),
  };

  return {
    lengths,
    capacities,
    // Provenance of the Drill Pipe OD/ID used above (source, drill string,
    // frozen flag). Display-only — the UI renders it beside the DP cells.
    drillPipe: dpGeom,
    // Provenance of the HWDP / DC OD/ID used above (display-only — the UI
    // renders it beside the HWDP/DC cells, same as drillPipe above). Unlike
    // Drill Pipe there is no session/well fallback chain for these: they
    // only ever come from the bound BHA's own components, so `source` is
    // "bha" when that component exists and null when it doesn't (never
    // fabricated as "bha" with nothing behind it).
    hwdpGeometry: { odIn: _num(hwdpComp?.odIn), idIn: _num(hwdpComp?.idIn), source: hwdpComp ? "bha" : null },
    dcGeometry:   { odIn: _num(dcComp?.odIn),   idIn: _num(dcComp?.idIn),   source: dcComp ? "bha" : null },
    // Surface Line Volume (pump -> drillstring piping) + its own stroke
    // count. Display-only, sourced from the Rig Data Sheet -- see
    // ./surfaceLineVolumeResolver.js. Not part of composerInput: it is
    // intentionally not folded into any existing composer.js total yet.
    surfaceLineVolume: {
      valueBbl: surfaceLineGeom.valueBbl,
      source:   surfaceLineGeom.source,
      strokes:  surfaceLineStrokes,
    },
    // Well/depth/mud context for the DerivedTable's read-only sections.
    // Every one of these is already computed above for other purposes;
    // this just exposes them so the operator can verify the inputs a
    // bound OpState/Well/Mud actually resolved to without navigating
    // away. Display-only -- none of this is new arithmetic and none of
    // it enters composerInput.
    wellContext: {
      currentMd, currentTvd,
      holeSize,
      casingId: _num(csgFirst?.idIn),
      shoeMd, shoeTvd,
      mudWeight,
      pumpDisplacement: pumpDisp,
      // BOP reference data (see ./bopContextResolver.js) -- resolved from
      // the Well Profile's or Rig Data Sheet's bopRefId + the Rig Data
      // Sheet's own surfaceBop working pressures.
      bopType:                bopCtx.bopType,
      bopNominalSize:         bopCtx.bopNominalSize,
      bopWorkingPressure:     bopCtx.bopWorkingPressure,
      bopAnnularWorkingPressure: bopCtx.annularWorkingPressure,
      bopRamWorkingPressure:  bopCtx.ramWorkingPressure,
      bopSource:              bopCtx.source,
    },
    // Convenience: full IWCF-shaped slots ready for the composer.
    composerInput: {
      lengths: {
        dp:               lengths.dp.value,
        hwdp:              lengths.hwdp.value,
        dc:                lengths.dc.value,
        dpHwdpOpenHole:    lengths.dpHwdpOpenHole.value,
        dcOpenHole:        lengths.dcOpenHole.value,
        dpInCasing:        lengths.dpInCasing.value,
        dcInCasing:        0,
      },
      capacities: {
        dpInternal:        capacities.dpInternal.value,
        hwdpInternal:      capacities.hwdpInternal.value,
        dcInternal:        capacities.dcInternal.value,
        dpInCasing:        capacities.dpInCasing.value,
        dpHwdpInOpenHole:  capacities.dpHwdpInOpenHole.value,
        dcInOpenHole:      capacities.dcInOpenHole.value,
        dcInCasing:        0,
      },
    },
  };
}

/** Returns true if ANY length/capacity carries source:"user". */
export function hasAnyOverride({ lengths, capacities }) {
  const isUser = (c) => c && c.source === "user";
  return Object.values(lengths || {}).some(isUser)
      || Object.values(capacities || {}).some(isUser);
}
